// @ts-check
// Cobranças do Asaas no painel de suporte: filtros por situação, forma de pagamento e vencimento (na URL), tabela
// com o dono de cada cobrança e o detalhe num diálogo com estorno, remoção e nota fiscal. A mesma tabela é
// reaproveitada na aba Cobranças da instituição.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { searchParamsState, updateSearchParams } from '../../../app/router.js';
import { fetchAdminPayments } from '../../../data/admin-finance.js';
import { Button } from '../../../ui/button.js';
import { showOperationsError } from '../operations-format.js';
import { AdminEmpty, AdminLoading, AdminPage, FilterDate, FilterField, FilterSelect, OwnerLink, PaymentStatusTag, ProviderNotice, billingTypeLabel, formatDay, formatInteger, formatMoney } from './admin-ui.js';
import { PaymentDetailDialog } from './payment-detail-dialog.js';
import { t } from '../../../i18n/index.js';

const STATUS_FILTERS = ['ALL', 'PENDING', 'RECEIVED', 'CONFIRMED', 'OVERDUE', 'REFUNDED', 'PARTIALLY_REFUNDED', 'CHARGEBACK_REQUESTED'];
const BILLING_TYPE_FILTERS = ['ALL', 'PIX', 'BOLETO', 'CREDIT_CARD'];

/** @param {string} key @param {Record<string, unknown>} [values] */
const text = (key, values) => t(`operations.admin.panel.payments.${key}`, values);

/**
 * @param {{ payments: any[], showOwner?: boolean, onOpen: (paymentId: string) => void }} props
 */
export function PaymentsTable({ payments, showOwner = true, onOpen }) {
  return html`<div class="settings-table admin-table">
    <div class="settings-table-row settings-table-header admin-payment-row" data-owner=${showOwner ? 'true' : 'false'}>
      ${showOwner ? html`<span>${text('owner')}</span>` : null}
      <span>${text('description')}</span><span>${text('dueDate')}</span><span>${text('paymentDate')}</span><span>${text('billingType')}</span>
      <span class="settings-table-number">${text('value')}</span><span>${text('status')}</span>
    </div>
    ${payments.map((payment) => html`<button key=${payment.id} type="button" class="settings-table-row admin-payment-row admin-row-button" data-owner=${showOwner ? 'true' : 'false'} onClick=${() => onOpen(payment.id)}>
      ${showOwner ? html`<span class="admin-cell-stack"><${OwnerLink} owner=${payment.owner} /></span>` : null}
      <span class="admin-cell-stack"><span class="admin-cell-ellipsis">${payment.description || payment.id}</span><span class="settings-table-muted settings-table-mono">${payment.id}</span></span>
      <span class="settings-table-muted">${formatDay(payment.dueDate)}</span>
      <span class="settings-table-muted">${formatDay(payment.clientPaymentDate ?? payment.paymentDate)}</span>
      <span class="settings-table-muted">${billingTypeLabel(payment.billingType)}</span>
      <span class="settings-table-number admin-cell-money">${formatMoney(payment.value)}</span>
      <span><${PaymentStatusTag} status=${payment.status} /></span>
    </button>`)}
  </div>`;
}

/**
 * Lista de cobranças com filtros e paginação; `tenantId` restringe à instituição (aba Cobranças da ficha).
 * @param {{ tenantId?: string, showOwner?: boolean, embedded?: boolean }} props
 */
export function PaymentsList({ tenantId, showOwner = true, embedded = false }) {
  const parameters = searchParamsState.value;
  const prefix = embedded ? 'pay_' : '';
  const read = (/** @type {string} */ name) => parameters.get(`${prefix}${name}`) ?? '';
  const status = STATUS_FILTERS.includes(read('status')) ? read('status') : 'ALL';
  const billingType = BILLING_TYPE_FILTERS.includes(read('billingType')) ? read('billingType') : 'ALL';
  const dueFrom = read('dueFrom');
  const dueTo = read('dueTo');
  const [page, setPage] = useState(/** @type {any} */ (null));
  const [offset, setOffset] = useState(0);
  const [openId, setOpenId] = useState(/** @type {string | null} */ (null));
  const [version, setVersion] = useState(0);

  useEffect(() => {
    setPage(null);
    fetchAdminPayments({ status: status === 'ALL' ? undefined : status, billingType: billingType === 'ALL' ? undefined : billingType, dueFrom, dueTo, tenantId, offset })
      .then(setPage).catch(showOperationsError);
  }, [status, billingType, dueFrom, dueTo, tenantId, offset, version]);

  const setFilter = (/** @type {string} */ name, /** @type {string} */ value) => {
    setOffset(0);
    updateSearchParams({ [`${prefix}${name}`]: value && value !== 'ALL' ? value : null });
  };

  return html`<div class="admin-list-block">
    <div class="admin-toolbar">
      <${FilterField} label=${text('status')}>
        <${FilterSelect} value=${status} ariaLabel=${text('status')} onChange=${(/** @type {string} */ value) => setFilter('status', value)}
          options=${STATUS_FILTERS.map((value) => ({ value, label: value === 'ALL' ? text('allStatuses') : t(`operations.admin.panel.paymentStatuses.${value}`) }))} />
      </${FilterField}>
      <${FilterField} label=${text('billingType')}>
        <${FilterSelect} value=${billingType} ariaLabel=${text('billingType')} onChange=${(/** @type {string} */ value) => setFilter('billingType', value)}
          options=${BILLING_TYPE_FILTERS.map((value) => ({ value, label: value === 'ALL' ? text('allBillingTypes') : billingTypeLabel(value) }))} />
      </${FilterField}>
      <${FilterField} label=${text('dueFrom')}><${FilterDate} value=${dueFrom} ariaLabel=${text('dueFrom')} onChange=${(/** @type {string} */ value) => setFilter('dueFrom', value)} /></${FilterField}>
      <${FilterField} label=${text('dueTo')}><${FilterDate} value=${dueTo} ariaLabel=${text('dueTo')} onChange=${(/** @type {string} */ value) => setFilter('dueTo', value)} /></${FilterField}>
    </div>
    ${page === null ? html`<${AdminLoading} rows=${4} />` : html`
      <${ProviderNotice} isProviderConfigured=${page.isProviderConfigured} />
      ${page.payments.length === 0
        ? html`<${AdminEmpty} icon="IconReceipt" title=${text('empty')} />`
        : html`<${PaymentsTable} payments=${page.payments} showOwner=${showOwner} onOpen=${setOpenId} />
          <div class="admin-pagination">
            <span class="settings-table-muted">${text('pageInfo', { from: page.offset + 1, to: page.offset + page.payments.length, total: formatInteger(page.totalCount) })}</span>
            <div class="admin-pagination-actions">
              <${Button} title=${text('previousPage')} variant="secondary" size="small" disabled=${page.offset === 0} onClick=${() => setOffset(Math.max(0, page.offset - page.limit))} />
              <${Button} title=${text('nextPage')} variant="secondary" size="small" disabled=${!page.hasMore} onClick=${() => setOffset(page.offset + page.limit)} />
            </div>
          </div>`}
    `}
    ${openId ? html`<${PaymentDetailDialog} paymentId=${openId} onClose=${() => setOpenId(null)} onChanged=${() => setVersion(version + 1)} />` : null}
  </div>`;
}

export function AdminPaymentsPage() {
  return html`<${AdminPage} icon="IconReceipt" title=${t('operations.admin.panel.sections.payments')} subtitle=${text('subtitle')} wide=${true}>
    <${PaymentsList} />
  </${AdminPage}>`;
}
